<div #editingForm class="surface-card border-round-md shadow-2 m-2 p-3">
  <form [formGroup]="editForm" class="p-fluid">
    <div class="flex align-items-center mb-3 pb-2 border-bottom-1 surface-border">
      <i [class]="getNodeIcon()" class="text-2xl text-primary mr-3"></i>
      <div>
        <h6 class="m-0 text-primary font-semibold">
          {{
            isNewItem()
              ? isVariableType()
                ? ('workflow.variables.tree.edit.variable.creating' | translate)
                : ('workflow.variables.tree.edit.group.creating' | translate)
              : isVariableType()
                ? ('workflow.variables.tree.edit.variable.editing' | translate)
                : ('workflow.variables.tree.edit.group.editing' | translate)
          }}
        </h6>
        @if (isVariableType()) {
          <small class="text-600 flex align-items-center">
            @let absolutePath = getAbsolutePath();
            {{ 'workflow.variables.tree.absolute.path' | translate }}:
            <span class="font-mono text-primary font-semibold">
              {{ absolutePath }}
            </span>
            <p-button
              icon="pi pi-copy"
              size="small"
              [text]="true"
              severity="secondary"
              class="p-button-sm"
              [pTooltip]="'workflow.variables.tree.copy.path.tooltip' | translate"
              tooltipPosition="top"
              (onClick)="copyAbsolutePath()"
            />
          </small>
        }
      </div>
    </div>

    <div class="grid">
      <div class="col-12 md:col-6">
        <div class="field mb-3">
          <label for="variable-name" class="font-semibold text-900 mb-2 block">
            {{ 'workflow.variables.tree.form.name.label' | translate }}
            <span class="text-red-500">*</span>
          </label>
          <input
            #nameInput
            id="variable-name"
            pInputText
            [formControl]="editForm.controls.key"
            [placeholder]="'workflow.variables.tree.form.name.placeholder' | translate"
            class="w-full"
            (keydown)="stopArrowPropagation($event)"
            [class.p-invalid]="editForm.controls.key.invalid && editForm.controls.key.touched"
          />
          @if (editForm.controls.key.invalid && editForm.controls.key.touched) {
            <small class="p-error block mt-1">
              {{ 'workflow.variables.tree.form.name.required' | translate }}
            </small>
          }
        </div>
      </div>

      <div class="col-12 md:col-6">
        <div class="field mb-3">
          <label for="variable-group" class="font-semibold text-900 mb-2 block">
            {{ 'workflow.variables.tree.form.group.label' | translate }}
          </label>
          <p-treeSelect
            id="variable-group"
            [formControl]="editForm.controls.parentGroup"
            [options]="treeDataService.groupOptions()"
            [placeholder]="'workflow.variables.tree.form.group.placeholder' | translate"
            selectionMode="single"
            [showClear]="true"
            [filter]="true"
            filterBy="label"
            [metaKeySelection]="false"
            (keydown)="stopArrowPropagation($event)"
            class="w-full"
          >
            <ng-template let-selectedGroup pTemplate="value">
              @if (selectedGroup) {
                <div class="text-sm">
                  {{ getUIGroupPath(selectedGroup.key) }}
                </div>
              } @else {
                <span class="text-gray-500">
                  {{ 'workflow.variables.tree.form.group.placeholder' | translate }}
                </span>
              }
            </ng-template>
          </p-treeSelect>
        </div>
      </div>

      @if (isVariableType()) {
        <div class="col-12 md:col-6">
          <div class="field mb-3">
            <label for="variable-value" class="font-semibold text-900 mb-2 block">
              {{ 'workflow.variables.tree.form.value.label' | translate }}
              <span class="text-red-500">*</span>
            </label>
            <p-inputGroup class="w-full">
              <input
                id="variable-value"
                pInputText
                [formControl]="editForm.controls.value"
                [placeholder]="'workflow.variables.tree.form.value.placeholder' | translate"
                class="font-mono"
                (keydown)="stopArrowPropagation($event)"
              />
              <button
                type="button"
                pButton
                icon="pi pi-code"
                class="p-button-outlined p-button-secondary"
                [pTooltip]="'workflow.variables.tree.form.code-editor' | translate"
                (click)="openCodeEditor(editForm.controls.value)"
              ></button>
            </p-inputGroup>
            @if (editForm.controls.value.invalid && editForm.controls.value.touched) {
              <small class="p-error block mt-1">
                {{ 'workflow.variables.tree.form.value.required' | translate }}
              </small>
            }
          </div>
        </div>

        <div class="col-12 md:col-6">
          <div class="field mb-3">
            <label for="variable-regex" class="font-semibold text-900 mb-2 block">
              {{ 'workflow.variables.tree.form.regex.label' | translate }}
            </label>
            <input
              id="variable-regex"
              pInputText
              [formControl]="editForm.controls.regexRule"
              [placeholder]="'workflow.variables.tree.form.regex.placeholder' | translate"
              class="w-full font-mono"
              (keydown)="stopArrowPropagation($event)"
            />
          </div>
        </div>
      }

      <div class="col-12">
        <div class="field mb-3">
          <label for="variable-description" class="font-semibold text-900 mb-2 block">
            {{ 'workflow.variables.tree.form.description.label' | translate }}
          </label>
          <p-inputGroup class="w-full">
            <input
              id="variable-description"
              pInputText
              [formControl]="editForm.controls.description"
              [placeholder]="'workflow.variables.tree.form.description.placeholder' | translate"
              class="w-full"
              (keydown)="stopArrowPropagation($event)"
            />
            <button
              type="button"
              pButton
              icon="pi pi-code"
              class="p-button-outlined p-button-secondary"
              [pTooltip]="'workflow.variables.tree.form.code-editor' | translate"
              [disabled]="editForm.controls.description.disabled"
              (click)="openCodeEditor(editForm.controls.description)"
            ></button>
          </p-inputGroup>
        </div>
      </div>
    </div>

    @let backendValidationError = store.lastError();
    @if (backendValidationError) {
      <div class="p-message p-message-error mb-3">
        <div class="p-message-wrapper">
          <div class="p-message-title">{{ backendValidationError.title }}</div>
          <div class="p-message-text">{{ backendValidationError.message }}</div>
        </div>
      </div>
    }

    <div class="flex gap-2 pt-2 border-top-1 surface-border">
      <p-button
        icon="pi pi-check"
        [label]="'workflow.variables.tree.form.save' | translate"
        severity="success"
        (onClick)="saveItem()"
        [disabled]="!editForm.valid || backendValidationError"
        class="flex-1"
      />
      <p-button
        icon="pi pi-times"
        [label]="'workflow.variables.tree.form.cancel' | translate"
        (onClick)="cancelEdit()"
        class="flex-1"
      />
    </div>
  </form>
</div>

<div style="visibility: hidden; max-height: 0px">
  <div #codeEditorContainer></div>
</div>
